import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from "@mui/material"
import { translate } from "app/language/service"
import { ISortOrder } from "app/model"
import { IForm } from "form/model"
import { onConfigurationFormChange } from "form/tools/configuration"
import { getComparator } from "form/tools/ssp"
import { synchronizeNewSspDealSelection } from "form/tools/ssp-deal"
import React from "react"
import { ICampaignSspDeal, ISspDeal } from "ssp/model"
import { OrderByArrow } from "ui/components/OrderByArrow"
import { stableSort } from "ui/tools/list"
import { ColumnHeader } from "../../../../ui/components/ColumnHeader"
import { SspDealRow } from "./SspDealRow"

interface ISspDealListProps {
  form: IForm
  allDeals: ISspDeal[]
  selectedDeals: ICampaignSspDeal[]
  rowRenderIndex: number
  sspIndex: number // used by tests
}

type IRowKey = keyof ISspDeal

export const SspDealList = React.memo(
  ({ form, allDeals, selectedDeals, rowRenderIndex, sspIndex }: ISspDealListProps): JSX.Element => {
    const [orderBy, setOrderBy] = React.useState<IRowKey>("dealid")
    const [order, setOrder] = React.useState<ISortOrder>("asc")
    const [selectedDealIds, setSelectedDealIds] = React.useState<number[]>(selectedDeals.map(d => d.ssp_deal_id))
    React.useEffect(() => {
      setSelectedDealIds(selectedDeals.map(s => s.ssp_deal_id))
    }, [rowRenderIndex])
    const {
      isLoadingAllOperators,
      isSynchronizingSsp,
      isLoadingOperatorsForecast,
      hasLoadingAllSspDealsError: hasLoadingAllDealsError
    } = form.screens
    const isCreateForm = form.context.isCreation
    const emptyMessage = allDeals.length === 0 ? translate("campaign.ssp-deal.not.available") : undefined
    const handleRequestSort = (_event: React.MouseEvent<unknown>, property: IRowKey) => {
      const isAsc = orderBy === property && order === "asc"
      setOrder(isAsc ? "desc" : "asc")
      setOrderBy(property)
    }
    return hasLoadingAllDealsError ? (
      <Typography
        color="error"
        fontSize=".85rem"
        sx={{ p: "1rem", pl: "5.5rem", borderBottom: theme => theme.palette.border }}>
        {translate("operator.loading.all.error")}
      </Typography>
    ) : isLoadingAllOperators ? (
      <Typography
        color="textSecondary"
        fontSize=".85rem"
        sx={{ p: "1rem", pl: "5.5rem", borderBottom: theme => theme.palette.border }}>
        {translate("operator.loading.all")}
      </Typography>
    ) : emptyMessage ? (
      <Typography
        color="textSecondary"
        fontSize=".85rem"
        sx={{ p: "1rem", pl: "5.5rem", borderBottom: theme => theme.palette.border }}>
        {emptyMessage}
      </Typography>
    ) : (
      <TableContainer
        sx={{
          "& .MuiTableCell-root": { pt: 0, pb: 0, fontSize: ".8rem", fontWeight: 300 },
          "& .MuiTableCell-root.MuiTableCell-head": {
            fontSize: ".85rem",
            fontWeight: 500
          }
        }}>
        <Table aria-label="collapsible table">
          <TableHead sx={{ backgroundColor: theme => theme.palette.background.paper }}>
            <TableRow
              sx={{
                "height": "3rem",
                "& .MuiTableSortLabel-icon": {
                  ml: "1rem",
                  mt: "-1rem"
                }
              }}>
              <TableCell width="1rem">{/* padding left */}</TableCell>
              <TableCell colSpan={2} sx={{ position: "relative" }}>
                <TableSortLabel
                  active={orderBy === "dealid"}
                  direction={orderBy === "dealid" ? order : "asc"}
                  onClick={(event: React.MouseEvent<unknown>) => handleRequestSort(event, "dealid")}>
                  <ColumnHeader primaryText={translate("label.dsp-deal.name")} />
                  <OrderByArrow order={order} currentOrderBy={orderBy} columnOrderBy="dealid" />
                </TableSortLabel>
              </TableCell>
              <TableCell colSpan={3}>
                <ColumnHeader primaryText={translate("label.description")} />
              </TableCell>
              <TableCell width="1rem">{/* padding right */}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(allDeals, getComparator(order, orderBy)).map((row, index) => (
              <SspDealRow
                key={row.id}
                row={row}
                sspIndex={sspIndex}
                sspDealIndex={index}
                isSelected={selectedDealIds.includes(row.id)}
                disabled={isSynchronizingSsp}
                isLoadingOperatorsForecast={isLoadingOperatorsForecast}
                isCreateForm={isCreateForm}
                onSelectionChange={(selectedRow, checked) => {
                  let newSelectedIds: number[] = checked
                    ? [...form.screens.campaignSspDeals.map(s => s.ssp_deal_id), selectedRow.id]
                    : form.screens.campaignSspDeals.map(s => s.ssp_deal_id).filter(id => id !== selectedRow.id)
                  synchronizeNewSspDealSelection(newSelectedIds)
                  setSelectedDealIds(newSelectedIds)
                  if (newSelectedIds.length === 0) {
                    onConfigurationFormChange(form, {
                      ...form.configuration,
                      main: { ...form.configuration.main, isBiddingOnlyOnDeals: false }
                    })
                  }
                }}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }
)
